import React, { Component } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import get from 'lodash/get';
import { withFormik } from 'formik';
import Yup from 'yup';
import FormField from 'cirrus/FormField';

import { entities } from 'pages/store/schema';
import { getValues, getProcessing, getErrors } from 'pages/store/selectors';
import { getData } from 'pages/store/actions/data';
import { actionTypes } from 'pages/store/actions/defaults';

import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import Group from '@lightspeed/cirrus-group';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '@lightspeed/cirrus-modal';
import Select from '@lightspeed/cirrus-select';
import Text from '@lightspeed/cirrus-text';

class LanguageModal extends Component {
  componentDidMount() {
    const { accountId } = this.props;

    this.props.getLanguagesData(`/staff/accounts/${accountId}/ecom/languages`);
  }

  render() {
    const {
      languageCode,
      languages,
      values,
      setFieldValue,
      errors,
      handleSubmit,
      isSubmitting,
      onClose,
      loadingErrors,
    } = this.props;

    return (
      <Modal className="cirrus responsive" isOpen onRequestClose={() => onClose()}>
        <ModalHeader onCloseClicked={() => onClose()}>eCom Default Language</ModalHeader>
        <ModalBody>
          <div style={{ maxWidth: '680px' }}>
            {loadingErrors ? (
              <Alert type="danger" className="cr-mb-2" noCloseBtn>
                <p className="cr-m-0">
                  {get(loadingErrors, 'response.data.errors[0].detail') || 'An error occurred'}
                </p>
              </Alert>
            ) : null}
            <Text body color="gray">
              <strong>WARNING</strong>: This operation cannot be undone, will resync all product
              data and may take hours to complete.{' '}
              <a
                href="https://confluence.atlightspeed.net/pages/viewpage.action?spaceKey=eCom&title=RAD+Support+Tool"
                target="_blank"
                rel="noopener noreferrer"
              >
                See full documentation
              </a>
              .
            </Text>
            <ul className="cr-mv-0 cr-pt-4">
              <li>
                <Text body color="gray">
                  Step 1: Ensure the new Default language exists in eCom.
                </Text>
              </li>
              <li>
                <Text body color="gray">
                  Step 2: Confirm that the merchant will not delete eCom Languages during the
                  resync.
                </Text>
              </li>
            </ul>
            <div className="cr-pv-4">
              <Text body>Changing the eCom Default Language will:</Text>
              <ul className="cr-mv-0">
                <li>
                  <Text body color="gray">
                    Copy all Retail Item Descriptions and Categories to the newly selected language;
                  </Text>
                </li>
                <li>
                  <Text body color="gray">
                    Moving forward we send data to the new language.
                  </Text>
                </li>
              </ul>
            </div>
            <form onSubmit={handleSubmit}>
              <FormField
                label="eCom Default Language"
                status={
                  errors.languageCode || errors.languageCode
                    ? {
                        type: 'error',
                        message: errors.languageCode || errors.languageCode,
                      }
                    : null
                }
              >
                <Select
                  disabled={!languages.length || isSubmitting}
                  value={values.languageCode}
                  onChange={event => {
                    const { value } = event.target;
                    setFieldValue('languageCode', value);
                  }}
                >
                  {!languages.length ? (
                    <option>Fetching eCom languages...</option>
                  ) : (
                    languages.map(language => (
                      <option key={language.code} value={language.code}>
                        {language.name}
                      </option>
                    ))
                  )}
                </Select>
              </FormField>
            </form>
          </div>
        </ModalBody>
        <ModalFooter>
          <Group className="cr-justify-end">
            <Button onClick={() => onClose()}>Cancel</Button>
            <Button
              variant="secondary"
              fill
              loading={isSubmitting}
              onClick={handleSubmit}
              disabled={!languages.length || values.languageCode === languageCode}
            >
              I understand. Change eCom Default Language
            </Button>
          </Group>
        </ModalFooter>
      </Modal>
    );
  }
}

const enhancedLanguageModal = withFormik({
  mapPropsToValues: props => ({
    languageCode: get(props, 'languageCode', ''),
  }),
  validationSchema: ({ languages }) =>
    Yup.object().shape({
      languageCode: Yup.string().oneOf(languages.map(language => language.code)),
    }),
  handleSubmit: async (values, formikBag) => {
    formikBag.setSubmitting(true);

    const { accountId } = formikBag.props;
    const { languageCode } = values;

    try {
      await axios.put(`/staff/accounts/${accountId}/ecom/language`, {
        data: {
          ecom: {
            language_code: languageCode,
          },
        },
      });
      formikBag.props.onConfirm({ updateData: true, message: 'Change Language pushed to queue' });
    } catch (e) {
      window.alertUser(get(e, 'response.data.errors[0].detail') || 'An error occurred');
    }

    formikBag.setSubmitting(false);
    formikBag.props.onClose();
  },
})(LanguageModal);

const mapStateToProps = state => ({
  languages: getValues(entities.staffEcomLanguage)(state),
  isLoading: !!getProcessing({
    entity: entities.staffEcomLanguage,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
  loadingErrors: getErrors({
    entity: entities.staffEcomLanguage,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
});

const mapDispatchToProps = dispatch => ({
  getLanguagesData: (url, options, onSuccess) =>
    dispatch(getData({ url, entity: entities.staffEcomLanguage, list: true, options, onSuccess })),
});

const ConnectedLanguageModal = connect(mapStateToProps, mapDispatchToProps)(enhancedLanguageModal);

export default ConnectedLanguageModal;
